import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { logout, clearTokens } from '../../api/client';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import PageHeader from '../../components/layout/PageHeader';
import {
  Mail,
  Phone,
  Building2,
  GraduationCap,
  Calendar,
  Briefcase,
  IndianRupee,
  LinkIcon,
  LogOut,
  Camera,
  Shield,
} from 'lucide-react';

interface InfoItemProps {
  icon: React.ReactNode;
  label: string;
  value?: string;
}

function InfoItem({ icon, label, value }: InfoItemProps): React.JSX.Element | null {
  if (!value) return null;
  return (
    <div className="flex items-center gap-4 p-4 rounded-xl hover:bg-slate-50 transition-colors">
      <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">{label}</p>
        <p className="text-sm font-semibold text-slate-900 truncate">{value}</p>
      </div>
    </div>
  );
}

export default function ProfileScreen(): React.JSX.Element {
  const { user, clearAuth } = useAuth();
  useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogout = async () => {
    try {
      setLoading(true);
      const refreshToken = localStorage.getItem('ecm_refresh_token');
      if (refreshToken) {
        await logout(refreshToken);
      }
    } catch {
      // ignore errors on logout
    } finally {
      clearTokens();
      await clearAuth();
      setLoading(false);
      setShowLogoutConfirm(false);
      navigate('/');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-500">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 animate-fade-in">
      <PageHeader title="Profile" subtitle="Manage your account and preferences" />

      {/* Profile card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden mb-6"
      >
        {/* Cover */}
        <div className="h-32 bg-amber-500 relative">
          <div className="absolute inset-0 gradient-mesh opacity-30" />
        </div>

        {/* Avatar & info */}
        <div className="px-6 pb-6">
          <div className="relative -mt-12 mb-4 flex items-end justify-between">
            <div className="relative">
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="w-24 h-24 rounded-2xl object-cover border-4 border-white shadow-lg"
                />
              ) : (
                <div className="w-24 h-24 rounded-2xl bg-amber-500 border-4 border-white shadow-lg flex items-center justify-center">
                  <span className="text-3xl font-bold text-white">
                    {user.name?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>
              )}
              <button className="absolute -bottom-1 -right-1 w-8 h-8 bg-white rounded-lg shadow-md flex items-center justify-center text-slate-600 hover:text-amber-500 transition-colors border border-slate-100">
                <Camera className="w-4 h-4" />
              </button>
            </div>
            <Badge variant="info" size="md">
              <Shield className="w-3.5 h-3.5 mr-1" />
              {user.role?.toUpperCase() || 'USER'}
            </Badge>
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-900">{user.name}</h2>
            <p className="text-slate-500 flex items-center gap-1 mt-1">
              <Mail className="w-4 h-4" />
              {user.email}
            </p>
          </div>

          {/* Info grid */}
          <div className="grid sm:grid-cols-2 gap-2">
            <InfoItem icon={<Phone className="w-5 h-5 text-amber-500" />} label="Mobile" value={user.mobile} />
            <InfoItem icon={<Calendar className="w-5 h-5 text-amber-500" />} label="Date of Birth" value={user.dob} />
            <InfoItem icon={<Building2 className="w-5 h-5 text-amber-500" />} label="Company" value={user.company} />
            <InfoItem icon={<GraduationCap className="w-5 h-5 text-amber-500" />} label="Education" value={user.education} />
            <InfoItem icon={<Calendar className="w-5 h-5 text-amber-500" />} label="Completion Year" value={user.completion_year} />
            <InfoItem icon={<Briefcase className="w-5 h-5 text-amber-500" />} label="Status" value={user.current_status} />
            <InfoItem icon={<IndianRupee className="w-5 h-5 text-amber-500" />} label="Salary" value={user.current_salary} />
            <InfoItem icon={<LinkIcon className="w-5 h-5 text-amber-500" />} label="LinkedIn" value={user.linkedin} />
          </div>
        </div>
      </motion.div>

      {/* Logout section */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6"
      >
        <h3 className="text-lg font-bold text-slate-900 mb-2">Account</h3>
        <p className="text-sm text-slate-500 mb-4">
          Sign out of your account on this device.
        </p>
        <Button
          variant="danger"
          onClick={() => setShowLogoutConfirm(true)}
          leftIcon={<LogOut className="w-4 h-4" />}
        >
          Sign Out
        </Button>
      </motion.div>

      {/* Logout confirmation modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl"
          >
            <h3 className="text-lg font-bold text-slate-900 mb-2">Sign Out?</h3>
            <p className="text-sm text-slate-500 mb-6">
              Are you sure you want to sign out of your account?
            </p>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setShowLogoutConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                className="flex-1"
                isLoading={loading}
                onClick={handleLogout}
              >
                Sign Out
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
